import { useEffect, useRef } from 'react';
import PropTypes from 'prop-types';

import Field, { useFieldIds } from './Field.jsx';

import './Choice.css';

/**
 * The three controls whose label sits beside the control rather than above it, so they render
 * their own `.field-label` inside the clickable `<label>` and leave Field's label slot unset.
 * Field still supplies the wrapper, the status colours and the message line.
 *
 * Each is a native input with `appearance: none` — the tick, dot and knob are drawn in CSS on the
 * input itself, so keyboard focus, checked state and disabled all come from the browser.
 *
 * `labelBefore` puts text on the leading side as well, for the Off/On pattern. Both sides sit
 * inside the clickable `<label>`, so both are part of the accessible name — pass `aria-label` when
 * the two together would not read as one ("Off On").
 */

const shared = {
    id: PropTypes.string,
    label: PropTypes.node,
    helpText: PropTypes.node,
    status: PropTypes.oneOf(['neutral', 'danger']),
    isRequired: PropTypes.bool,
    isDisabled: PropTypes.bool,
    name: PropTypes.string,
    checked: PropTypes.bool,
    onChange: PropTypes.func,
    // Text on the leading side, for the Off/On pattern.
    labelBefore: PropTypes.node,
    'aria-label': PropTypes.string,
};

const ChoiceLabel = ({ text, isRequired }) => (
    text ? (
        <span className="field-label">
            {text}
            {isRequired && <span className="field-required">*</span>}
        </span>
    ) : null
);

/* ── Checkbox ── */

export const Checkbox = ({
    id,
    label,
    labelBefore,
    helpText,
    status = 'neutral',
    isRequired = false,
    isDisabled = false,
    isIndeterminate = false,
    name,
    value,
    checked,
    onChange = () => {},
    'aria-label': ariaLabel,
}) => {
    const { fieldId, messageId } = useFieldIds(id, !!helpText);
    const inputRef = useRef(null);

    useEffect(() => {
        if (inputRef.current) inputRef.current.indeterminate = isIndeterminate;
    }, [isIndeterminate]);

    return (
        <Field id={fieldId} helpText={helpText} status={status} isDisabled={isDisabled}>
            <label className="choice">
                <ChoiceLabel text={labelBefore} />
                <input
                    ref={inputRef}
                    id={fieldId}
                    className="choice-input choice-input--check"
                    type="checkbox"
                    name={name}
                    value={value}
                    checked={checked}
                    disabled={isDisabled}
                    required={isRequired}
                    aria-label={ariaLabel}
                    aria-invalid={status === 'danger' || undefined}
                    aria-describedby={messageId}
                    onChange={onChange}
                />
                <ChoiceLabel text={label} isRequired={isRequired} />
            </label>
        </Field>
    );
};

Checkbox.propTypes = {
    ...shared,
    isIndeterminate: PropTypes.bool,
    value: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
};

/* ── Radio ── */

export const RadioButton = ({
    id,
    label,
    labelBefore,
    helpText,
    status = 'neutral',
    isRequired = false,
    isDisabled = false,
    name,
    value,
    checked,
    onChange = () => {},
    'aria-label': ariaLabel,
}) => {
    const { fieldId, messageId } = useFieldIds(id, !!helpText);

    return (
        <Field id={fieldId} helpText={helpText} status={status} isDisabled={isDisabled}>
            <label className="choice">
                <ChoiceLabel text={labelBefore} />
                <input
                    id={fieldId}
                    className="choice-input choice-input--radio"
                    type="radio"
                    name={name}
                    value={value}
                    checked={checked}
                    disabled={isDisabled}
                    required={isRequired}
                    aria-label={ariaLabel}
                    aria-invalid={status === 'danger' || undefined}
                    aria-describedby={messageId}
                    onChange={onChange}
                />
                <ChoiceLabel text={label} isRequired={isRequired} />
            </label>
        </Field>
    );
};

RadioButton.propTypes = {
    ...shared,
    value: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
};

/* ── Toggle ── */

export const ToggleSwitch = ({
    id,
    label,
    labelBefore,
    helpText,
    status = 'neutral',
    isRequired = false,
    isDisabled = false,
    name,
    checked,
    onChange = () => {},
    'aria-label': ariaLabel,
}) => {
    const { fieldId, messageId } = useFieldIds(id, !!helpText);

    return (
        <Field id={fieldId} helpText={helpText} status={status} isDisabled={isDisabled}>
            <label className="choice">
                <ChoiceLabel text={labelBefore} />
                <input
                    id={fieldId}
                    className="choice-input choice-input--toggle"
                    type="checkbox"
                    role="switch"
                    name={name}
                    checked={checked}
                    disabled={isDisabled}
                    required={isRequired}
                    aria-label={ariaLabel}
                    aria-invalid={status === 'danger' || undefined}
                    aria-describedby={messageId}
                    onChange={onChange}
                />
                <ChoiceLabel text={label} isRequired={isRequired} />
            </label>
        </Field>
    );
};

ToggleSwitch.propTypes = shared;
